import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Paper,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomAlert from "../Common/Components/CustomAlert";
import { useSelector } from "react-redux";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SaveIcon from "@mui/icons-material/Save";

function CreateEmployee(props) {
  const { t } = useTranslation(["Employee"], ["Common"]);
  const [formData, setFormData] = useState({
    lastname: "",
    name: "",
    username: "",
    email: "",
    nodooliz: "",
    language: "",
    department: "",
  });

  const [departments, setDepartments] = useState([]);
  const [languages] = useState(["FR", "EN"]);
  const [showAlert, setShowAlert] = useState(false);
  const [typeMessage, setTypeMessage] = useState("");
  const [message, setMessage] = useState("");
  const userState = useSelector((state) => state.user);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [existingEmail, setExistingEmail] = useState(null);

  const handleChange = (field) => (event) => {
    const newValue = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    if (field === "name" || field === "lastname") {
      if (formData.name && formData.lastname) {
        GenerateUsername();
      }
    }
  };

  const GenerateUsername = async () => {
    var userInfo = {
      Firstname: formData.name,
      Lastname: formData.lastname,
    };
    fetch(`${process.env.REACT_APP_API_URL}/employee/GenerateUsername`, {
      method: "POST",
      withCredentials: true,
      credentials: "include",

      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userState.token}`,
      },
      body: JSON.stringify(userInfo),
    })
      .then((results) => {
        return results.text();
      })
      .then((data) => {
        setFormData((prev) => ({
          ...prev,
          username: data,
        }));
      });
  };

  const handleCreateEmployee = async () => {
    // Create the new employee object
    const newEmployee = {
      noEmplacement: userState.location.noEmplacement,
      username: formData.username,
      name: formData.name,
      lastName: formData.lastname,
      email: formData.email,
      langue: formData.language,
      noDooliz: formData.nodooliz,
      department: formData.department,
    };

    try {
      // Validate employee information
      if (validateEmployee() && uniqueEmail() && uniqueUsername()) {
        // Send POST request to create employee
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/employee/CreateEmployeeRestaurant`,
          {
            method: "POST",
            credentials: "include", // Send cookies if needed
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`, // Ensure token is valid
            },
            body: JSON.stringify(newEmployee), // Convert employee object to JSON
          }
        );

        // Check if the response was successful
        const data = await response.text();
        if (response.ok) {
          props.closeModal();
          setMessage("User created successfully");
          setTypeMessage("success");
          setShowAlert(true);
          window.location.reload(); // Reload the page if successful
        } else {
          throw new Error(data || "Error creating user");
        }
      }
    } catch (error) {
      console.error("Error creating employee:", error);
      setMessage(error.message || "An error occurred");
      setTypeMessage("error");
      setShowAlert(true);
    }
  };

  const GetExternalDepartment = async () => {
    fetch(
      `${process.env.REACT_APP_API_URL}/Localization/GetExternalDepartment`,
      {
        method: "GET",
        withCredentials: true,
        credentials: "include",

        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
      }
    )
      .then((results) => {
        return results.json();
      })
      .then((data) => {
        setDepartments(data.filter((d) => d.id !== 7));
      });
  };

  const validateEmployee = () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.lastname ||
      !formData.language ||
      !formData.department
    ) {
      setMessage(
        "Please complete at least the name, the lastname, the language, the email and the department for this new user"
      );
      setTypeMessage("echec");
      setShowAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (formData.name && formData.lastname) {
      GenerateUsername();
    }
  }, [formData.name, formData.lastname]);

  useEffect(() => {
    GetExternalDepartment();
  }, []);

  const uniqueEmail = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/UniqueEmail/${formData.email}`,
        {
          method: "GET",
          withCredentials: true,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );

      const data = await response.json();

      if (data === false) {
        setOpenConfirmDialog(true);
        setExistingEmail(formData.email);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error checking email:", error);
      setMessage("Error checking email");
      setTypeMessage("error");
      setShowAlert(true);
      return false;
    }
  };

  const handleAddExistingUser = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/AddExistingUserToRestaurant`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify({
            email: existingEmail,
            noEmplacement: props.noEmplacement,
            department: formData.department,
          }),
        }
      );

      if (response.ok) {
        setMessage("User added successfully");
        setTypeMessage("success");
        setShowAlert(true);
        props.closeModal();
        window.location.reload();
      } else {
        throw new Error("Failed to add user");
      }
    } catch (error) {
      setMessage("Error adding user");
      setTypeMessage("error");
      setShowAlert(true);
    } finally {
      setOpenConfirmDialog(false);
    }
  };

  const uniqueUsername = async () => {
    fetch(
      `${process.env.REACT_APP_API_URL}/employee/UniqueUsername/${formData.username}`,
      {
        method: "GET",
        withCredentials: true,
        credentials: "include",

        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
      }
    )
      .then((results) => {
        return results.json();
      })
      .then((data) => {
        if (data === false) {
          setMessage("username already used");
          setTypeMessage("echec");
          setShowAlert(true);
        } else {
          return true;
        }
      });
  };

  return (
    <>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <PersonAddIcon color="primary" />
        <Typography variant="h5" component="h2">
          {t("CreateEmployeeHeader")}
        </Typography>
      </Box>
      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              value={formData.lastname}
              onChange={handleChange("lastname")}
              variant="outlined"
              size="small"
              fullWidth
              label={t("Lastname")}
              sx={{ mb: 2 }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              value={formData.name}
              onChange={handleChange("name")}
              variant="outlined"
              size="small"
              fullWidth
              label={t("Firstname")}
              sx={{ mb: 2 }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              value={formData.username}
              onChange={handleChange("username")}
              variant="outlined"
              size="small"
              fullWidth
              label={t("Username")}
              sx={{ mb: 2 }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              value={formData.email}
              onChange={handleChange("email")}
              variant="outlined"
              size="small"
              fullWidth
              label={t("Email")}
              type="email"
              sx={{ mb: 2 }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <TextField
              value={formData.nodooliz}
              onChange={handleChange("nodooliz")}
              variant="outlined"
              size="small"
              fullWidth
              label={t("NoDooliz")}
              sx={{ mb: 2 }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("Language")}</InputLabel>
            <Select
              value={formData.language}
              onChange={handleChange("language")}
              label={t("Language")}
              sx={{ mb: 2 }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("Department")}</InputLabel>
            <Select
              value={formData.department}
              onChange={handleChange("department")}
              label={t("Department")}
              sx={{ mb: 2 }}
            >
              {departments.map((dept, index) => (
                <MenuItem key={index} value={dept}>
                  {userState.lang === "EN" ? dept.textEn : dept.textFr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={props.closeModal} color="inherit">
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateEmployee}
          startIcon={<SaveIcon />}
          color="primary"
        >
          {t("Save")}
        </Button>
      </Box>

      <Dialog
        open={openConfirmDialog}
        disableEscapeKeyDown
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          {t("Existing User Found")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ py: 2 }}>
            {t(
              "This email is already associated with an existing user. Would you like to add this user to your restaurant?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => {
              setOpenConfirmDialog(false);
              setMessage(t("Operation cancelled"));
              setTypeMessage("info");
              setShowAlert(true);
            }}
            variant="outlined"
            color="inherit"
          >
            {t("No")}
          </Button>
          <Button
            onClick={handleAddExistingUser}
            variant="contained"
            color="primary"
            autoFocus
          >
            {t("Yes")}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomAlert
        open={showAlert}
        onClose={() => setShowAlert(false)}
        severity={typeMessage}
        message={message}
      />
    </>
  );
}

export default CreateEmployee;
